import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// Alto del Navbar (h-20 = 5rem = 80px). El overlay se posiciona
// debajo para que el Navbar siga visible y clickeable.
const NAVBAR_HEIGHT = 80;

/**
 * Transición "wipe/expand" desde una columna del Kanban hacia
 * el tablero detallado.
 *
 * El `OperationKanban` pasa en `location.state.fromRect` el
 * `getBoundingClientRect()` de la columna clickeada. Este
 * componente:
 *
 *  1. Renderiza el contenido del board con fade-in (aparece
 *     inmediatamente, no se oculta durante la animación).
 *  2. Renderiza un overlay con `position: fixed` que se expande
 *     DESDE el centro de la columna (escala de 0 a 1) y se
 *     desvanece (opacity 1 a 0). Esto da la sensación de que
 *     la columna "se abre" y se convierte en el tablero.
 *
 * El `transform-origin` del overlay se calcula en píxeles del
 * viewport (con offset del navbar) para que la expansión
 * arranque exactamente desde la posición de la columna.
 *
 * Fallback (deep-link, refresh, sin `fromRect`): entrada simple
 * con fade + slide-up.
 */
const BoardPageTransition = ({ children }) => {
  const location = useLocation();
  const fromRect = location.state?.fromRect;

  if (!fromRect || typeof window === 'undefined') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    );
  }

  // Centro de la columna en coords del wrapper (viewport - navbar).
  // El `transform-origin` del overlay usará este punto como origen
  // de la escala.
  const centerX = fromRect.left + fromRect.width / 2;
  const centerY = fromRect.top - NAVBAR_HEIGHT + fromRect.height / 2;

  return (
    <>
      {/* 1) Contenido del board con fade-in (aparece de inmediato) */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.3, ease: 'easeOut' }}
      >
        {children}
      </motion.div>

      {/* 2) Overlay que se expande desde la columna y se desvanece.
          `pointerEvents: none` para no bloquear clicks. Al terminar,
          queda transparente y no molesta. */}
      <motion.div
        aria-hidden
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 1, opacity: 0 }}
        transition={{
          scale: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: 0.35, delay: 0.2, ease: 'easeOut' },
        }}
        style={{
          position: 'fixed',
          top: NAVBAR_HEIGHT,
          left: 0,
          right: 0,
          bottom: 0,
          // Por debajo del Navbar (z-50) y de los Dialog/AlertDialog
          // de Radix (z-50), pero por encima del resto del layout.
          zIndex: 30,
          backgroundColor: 'hsl(var(--card))',
          transformOrigin: `${centerX}px ${centerY}px`,
          pointerEvents: 'none',
          willChange: 'transform, opacity',
        }}
      />
    </>
  );
};

export default BoardPageTransition;
